// Camera focus for routed links (user 2026-10-10): "when you come in with a planet link the camera
// moves to that planet and zooms in a way the entire planet is in the center; same for a
// settlement — in SPA the link just opens the dialog, in SSR the map loads at that sector and the
// camera focuses on the settlement". Desktop: MapControls target + camera on its current viewing
// direction at a distance that fits the planet; XR: the camera rig in front of the planet.
import { Vector3 } from 'three';

// scratch vectors (reused, never returned)
const _p = new Vector3();
const _dir = new Vector3();
const _t = new Vector3();

// the planet entity of sector [x, y] with that index, waiting for the sector to load (SSR entry:
// the grid draws after the page; SPA: the sector may still be loading after a jump)
export async function waitForPlanet(x, y, index, timeoutMs = 15000) {
	const t0 = performance.now();
	for (;;) {
		const entities = game.entities.getSector([x, y]) || [];
		const planet = entities.find((e) => e && e.$type === 'planet' && e.index === index && e.mesh);
		if (planet) {
			return planet;
		}
		if (performance.now() - t0 > timeoutMs) {
			return null;
		}
		await sleep(200);
	}
}

// world position of a tile centre on a planet. The centres are local to the renderer's INNER
// group (the builder's scaled object3d — unit coords for next, noise-radius coords for legacy),
// the frame the settlements are placed in (planet.js createSettlement) — NOT planet.mesh:
// converting through the outer mesh skipped that scale and on the phone (legacy) put the
// settlement target ~60 000 units out in empty space (2026-10-12 /0/0/3/177 deep link).
function tileWorldPosition(planet, tileIndex) {
	const renderer = planet.renderer;
	const centers = renderer?.tiles?.centers;
	const c = centers && centers[tileIndex];
	const frame = renderer?.settlements?.parent ? renderer.settlements : null;
	if (!c || !frame) {
		return null;
	}
	frame.updateWorldMatrix(true, false);
	return frame.localToWorld(_t.set(c[0], c[1], c[2]).clone());
}

// yaw of the headset pose inside the rig (camera.quaternion is the local pose in XR)
function _headYaw(camera) {
	_t.set(0, 0, -1).applyQuaternion(camera.quaternion);
	return Math.atan2(-_t.x, -_t.z);
}

// put the viewer in front of `target` (world) so a sphere of radius r (world) spans `span` degrees;
// `fromDir` (world, unit): desktop looks along it (a settlement: down its tile's surface normal, so
// the city is centred instead of on the horizon); absent → the current viewing direction
function viewFrom(targetWorld, rWorld, span, fromDir = null) {
	const camera = game.camera;
	const controls = game.controls;
	const rig = game.cameraRig;
	const distance = Math.max(rWorld * 1.05, rWorld / Math.tan((span / 2) * Math.PI / 180));
	if (game.renderer.xr.isPresenting && rig) {
		// keep the viewer on its current side of the target, slightly above it. The HEAD is what
		// must end up at `distance` and face the target: the camera sits at the headset pose inside
		// the rig (≈1.6 m up, any yaw), so the rig is placed and turned to compensate for it.
		camera.getWorldPosition(_p);
		_dir.copy(_p).sub(targetWorld);
		// the target straight ahead at EYE LEVEL: it sits in the HUD frame's hole (the panel hangs
		// 0.15 m below the eyes)
		_dir.y = 0;
		if (_dir.lengthSq() < 1e-6) {
			_dir.set(0, 0, 1);
		}
		_dir.normalize();
		const headYaw = _headYaw(camera);
		rig.rotation.set(0, Math.atan2(_dir.x, _dir.z) - headYaw, 0);
		rig.updateMatrixWorld(true);
		// desired head position minus the head's offset inside the (now turned) rig
		_t.copy(camera.position).applyQuaternion(rig.quaternion);
		rig.position.copy(targetWorld).addScaledVector(_dir, distance).sub(_t);
		game.recenterHud?.(); // the lazy-following HUD snaps to the new heading, so its hole frames the target
		return { distance, xr: true };
	}
	// desktop: the given direction or the same viewing direction as now, the target becomes the orbit target
	if (fromDir) {
		_dir.copy(fromDir);
	} else {
		_dir.copy(camera.position).sub(controls.target);
	}
	if (_dir.lengthSq() < 1e-6) {
		_dir.set(0, 2, 1);
	}
	_dir.normalize();
	controls.target.copy(targetWorld);
	camera.position.copy(targetWorld).addScaledVector(_dir, distance);
	controls.update();
	// an open HUD page covers one side of the viewport (desktop: the left column, hud-panels.js)
	// — centre the target in the REST of the screen, not under the page (user 2026-10-16: "still
	// not focusing the camera on the city"). Camera + orbit target slide sideways together.
	const shiftPx = pageShiftPx();
	if (shiftPx) {
		camera.updateMatrixWorld(true);
		const pxPerUnit = window.innerHeight / (2 * distance * Math.tan((camera.fov / 2) * Math.PI / 180));
		_t.setFromMatrixColumn(camera.matrixWorld, 0).normalize(); // camera right, world
		camera.position.addScaledVector(_t, -shiftPx / pxPerUnit);
		controls.target.addScaledVector(_t, -shiftPx / pxPerUnit);
		controls.update();
	}
	return { distance, xr: false, shiftPx };
}

// how far right (px, negative = left) the screen centre of the area NOT covered by the open HUD
// page lies from the viewport centre; 0 without a page, with a hidden HUD or when the page leaves
// no clear side (phone: one column). The page is a mesh tree under game.gui (a camera child), so
// its screen box comes from projecting its meshes through the game camera.
function pageShiftPx() {
	const page = game.hudPanels?.page?.();
	if (!page) {
		return 0;
	}
	for (let n = page; n && n !== game.gui; n = n.parent) {
		if (n.visible === false) {
			return 0;
		}
	}
	if (game.gui?.visible === false) {
		return 0;
	}
	const camera = game.camera;
	camera.updateMatrixWorld(true);
	page.updateWorldMatrix(true, true);
	const w = window.innerWidth;
	let x0 = Infinity;
	let x1 = -Infinity;
	page.traverse((o) => {
		if (!o.isMesh || !o.geometry) {
			return;
		}
		if (!o.geometry.boundingBox) {
			o.geometry.computeBoundingBox();
		}
		const bb = o.geometry.boundingBox;
		if (!bb || !Number.isFinite(bb.min.x)) {
			return;
		}
		for (let i = 0; i < 8; i++) {
			_t.set(i & 1 ? bb.max.x : bb.min.x, i & 2 ? bb.max.y : bb.min.y, i & 4 ? bb.max.z : bb.min.z)
				.applyMatrix4(o.matrixWorld)
				.project(camera);
			const px = ((_t.x + 1) / 2) * w;
			x0 = Math.min(x0, px);
			x1 = Math.max(x1, px);
		}
	});
	if (!Number.isFinite(x0)) {
		return 0;
	}
	// the larger free side (the desktop page's frame reaches a little past the half width);
	// under 30 % of the width nothing is gained by centring there
	const freeLeft = Math.max(0, x0);
	const freeRight = Math.max(0, w - x1);
	if (Math.max(freeLeft, freeRight) < w * 0.3) {
		return 0;
	}
	return freeRight >= freeLeft
		? Math.round((x1 + w) / 2 - w / 2) // page on the left: centre of [x1, w]
		: Math.round(x0 / 2 - w / 2); // page on the right: centre of [0, x0]
}

// the whole planet centred, filling ~60 % of the view height
export async function focusPlanet({ x, y, index, span = 30 }) {
	const planet = await waitForPlanet(Number(x), Number(y), Number(index));
	if (!planet) {
		return null;
	}
	planet.mesh.getWorldPosition(_p);
	const r = (planet.r || 1) * planet.mesh.getWorldScale(_t).x;
	const view = viewFrom(_p.clone(), r, span);
	return { planet: planet.name, r, ...view };
}

// the settlement's tile centred, close enough to see the city (a third of the planet's size).
// The tile positions exist only once the planet's detail renderer is built, which happens when the
// camera is near — so: whole planet first, then the tile as soon as the tiles are there.
export async function focusSettlement({ x, y, index, tile, span = 30, tilesTimeoutMs = 12000 }) {
	const first = await focusPlanet({ x, y, index, span });
	if (!first) {
		return null;
	}
	const t0 = performance.now();
	for (;;) {
		const planet = await waitForPlanet(Number(x), Number(y), Number(index), 1000);
		const target = planet && tileWorldPosition(planet, Number(tile));
		if (target) {
			const r = (planet.r || 1) * planet.mesh.getWorldScale(_t).x;
			const normal = target.clone().sub(planet.mesh.getWorldPosition(_p)).normalize();
			const view = viewFrom(target, r * 0.35, span, normal);
			return { planet: planet.name, tile: Number(tile), r, onTile: true, ...view };
		}
		if (performance.now() - t0 > tilesTimeoutMs) {
			return { ...first, onTile: false };
		}
		await sleep(250);
	}
}

// an SSR entry on a planet / settlement link (client.js, after the grid is drawn). The planet
// keeps orbiting after the one-time focus, so the view then FOLLOWS it (user 2026-10-12: "when
// i reload the link it's supposed to be on the planet") until the player moves the camera or
// leaves the page.
export async function focusFromUrl() {
	const m = location.pathname.match(/^\/(-?\d+)\/(-?\d+)\/(\d+)(?:\/(\d+))?/);
	if (!m) {
		return null;
	}
	const [, x, y, index, tile] = m;
	await waitForPage(); // the routed page's width decides where the target is centred (pageShiftPx)
	const result = tile != null
		? await focusSettlement({ x, y, index, tile })
		: await focusPlanet({ x, y, index });
	if (result) {
		followPlanet(await waitForPlanet(Number(x), Number(y), Number(index), 1000));
	}
	return result;
}

// move the viewer (desktop: camera + orbit target; XR: the rig) by the planet's displacement
// every frame, so the planet stays where the focus put it
let following = null;

export function followPlanet(planet) {
	stopFollowing();
	if (!planet || !planet.mesh) {
		return;
	}
	const last = planet.mesh.getWorldPosition(new Vector3());
	const now = new Vector3();
	const page = location.pathname;
	function tick() {
		if (location.pathname !== page || !planet.mesh || !planet.mesh.parent) {
			stopFollowing();
			return;
		}
		planet.mesh.getWorldPosition(now);
		_dir.copy(now).sub(last);
		if (_dir.lengthSq() === 0) {
			return;
		}
		if (game.renderer.xr.isPresenting && game.cameraRig) {
			game.cameraRig.position.add(_dir);
		} else {
			game.camera.position.add(_dir);
			game.controls.target.add(_dir);
			game.controls.update();
		}
		last.copy(now);
	}
	function stop() {
		stopFollowing();
	}
	game.controls.addEventListener('start', stop); // the player takes over the camera
	game.onFrame(tick);
	following = { tick, stop };
}

export function stopFollowing() {
	if (!following) {
		return;
	}
	game.offFrame(following.tick);
	game.controls.removeEventListener('start', following.stop);
	following = null;
}

// the HUD frame adopts the routed page shortly after the grid is drawn; a few seconds at most
async function waitForPage(timeoutMs = 4000) {
	const t0 = performance.now();
	while (!game.hudPanels?.page?.() && performance.now() - t0 < timeoutMs) {
		await sleep(100);
	}
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
